// Aug-2026 — Project Workspace: dedicated per-project operations screen opened
// by clicking a Project Code/Name on the Projects list. Lives inside the global
// multi-tab system as ONE tab, with its own fixed inner navigation bar:
// [BOQ] [Vendor Management] [Work Progress] [Project Stock].
// The project context (id) never changes while switching inner tabs.
import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge } from "@/components/ui-helpers";
import { X, FileSpreadsheet, ShoppingCart, HardHat, Boxes, ShieldAlert, PackageCheck } from "lucide-react";
import ProjectBoqTab from "@/components/ProjectBoqTab";
import ProjectPRTab from "@/components/ProjectPRTab";
import ProjectGRNTab from "@/components/ProjectGRNTab";
import ProjectStockTab from "@/components/ProjectStockTab";
import ProjectWorkProgressTab from "@/components/ProjectWorkProgressTab";

const TAB_COLOR = {
  boq: { active: "bg-sky-50 border-sky-600 text-sky-900", icon: "text-sky-600" },
  vendor: { active: "bg-orange-50 border-orange-600 text-orange-900", icon: "text-orange-600" },
  grn: { active: "bg-violet-50 border-violet-600 text-violet-900", icon: "text-violet-600" },
  stock: { active: "bg-teal-50 border-teal-600 text-teal-900", icon: "text-teal-600" },
  progress: { active: "bg-emerald-50 border-emerald-600 text-emerald-900", icon: "text-emerald-600" },
};

const OpsDisabledNotice = ({ testid }) => (
  <div className="border border-amber-300 bg-amber-50 rounded-lg p-6 flex items-start gap-3" data-testid={testid}>
    <ShieldAlert size={18} className="text-amber-600 mt-0.5 shrink-0" />
    <div>
      <div className="text-sm font-bold text-amber-900">Project Operations not enabled for this project</div>
      <div className="text-xs text-amber-800 mt-1">
        An administrator must enable Project Operations (globally and for this project) before this module becomes available.
      </div>
    </div>
  </div>
);

const Placeholder = ({ icon: Icon, title, message, testid }) => (  // eslint-disable-line no-unused-vars
  <div className="border border-slate-200 bg-slate-50/60 rounded-lg py-16 px-6 flex flex-col items-center text-center" data-testid={testid}>
    <Icon size={28} className="text-slate-400 mb-3" />
    <div className="text-sm font-bold text-slate-700">{title}</div>
    <div className="text-xs text-slate-500 mt-1 max-w-md">{message}</div>
  </div>
);

export default function ProjectWorkspace({ project, opsGlobalOn, onClose }) {
  const { hasPerm } = useAuth();
  const opsEnabled = !!opsGlobalOn && !!project.project_ops_enabled;
  // UX-1: flags OFF never hide history — projects that were EVER activated
  // (field present) render read-only; mutations are blocked server-side.
  const readOnly = !opsEnabled
    && project.project_ops_enabled !== undefined
    && project.project_ops_enabled !== null;
  const showTabs = opsEnabled || readOnly;

  // Jun-2026 — every project-ops tab now gates on its OWN permission key
  // (the temporary shared-visibility rule ended when WP-A shipped).
  const tabs = useMemo(() => {
    const t = [];
    if (hasPerm("project_boq", "view")) t.push({ key: "boq", label: "BOQ", icon: FileSpreadsheet });
    if (hasPerm("project_pr", "view")) t.push({ key: "vendor", label: "Vendor Management", icon: ShoppingCart });
    // Jun-2026 — Phase 3C: GRN + Project Stock now gate on their OWN keys.
    if (hasPerm("project_grn", "view")) t.push({ key: "grn", label: "GRN", icon: PackageCheck });
    if (hasPerm("project_stock", "view")) t.push({ key: "stock", label: "Project Stock", icon: Boxes });
    // Jun-2026 — WP-A: Work Progress gates on its own key.
    if (hasPerm("work_progress", "view")) t.push({ key: "progress", label: "Work Progress", icon: HardHat });
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [active, setActive] = useState(tabs[0]?.key || "");
  // Keep once-visited inner tabs MOUNTED (display:none) so upload previews,
  // open PR forms and scroll positions survive switching — mirrors the outer
  // TabsContext behaviour.
  const [visited, setVisited] = useState(() => new Set(tabs[0] ? [tabs[0].key] : []));
  const activate = (key) => {
    setVisited((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
    setActive(key);
  };

  return (
    <div className="space-y-0" data-testid="project-workspace">
      {/* Sticky workspace header + inner nav bar */}
      <div className="sticky top-0 z-20 bg-white border border-slate-200 rounded-t-lg shadow-sm">
        <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-2">
          <div className="min-w-0 flex items-center gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-900 font-mono" data-testid="workspace-project-code">{project.project_code}</span>
                <StatusBadge status={project.status} />
              </div>
              <div className="text-base md:text-lg font-bold text-slate-900 truncate" data-testid="workspace-project-name" title={`${project.project_name}${project.client_name ? ` · ${project.client_name}` : ""}`}>
                {project.project_name}{project.client_name ? <span className="text-slate-700"> · {project.client_name}</span> : null}
              </div>
            </div>
          </div>
          {onClose && (
            <button data-testid="workspace-close" onClick={onClose} title="Close workspace"
              className="text-slate-500 hover:text-slate-800 shrink-0"><X size={16} /></button>
          )}
        </div>
        <div className="flex items-end gap-1 px-2 border-t border-slate-100 overflow-x-auto" data-testid="workspace-tabbar">
          {tabs.map(({ key, label, icon: Icon }) => {
            const c = TAB_COLOR[key] || TAB_COLOR.boq;
            return (
              <button
                key={key}
                data-testid={`workspace-tab-${key}`}
                onClick={() => activate(key)}
                className={`shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 sm:gap-2 px-3 py-2.5 sm:px-5 sm:py-3 text-xs sm:text-sm font-bold border-b-[3px] -mb-px rounded-t-md transition-colors ${
                  active === key
                    ? c.active
                    : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50"
                }`}
              >
                <Icon size={16} className={c.icon} /> {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Inner tab panels — visited panels stay mounted, hidden via display:none */}
      <div className="border border-t-0 border-slate-200 rounded-b-lg bg-white p-2 sm:p-4">
        {tabs.length === 0 && (
          <div className="border border-slate-200 bg-slate-50/60 rounded-lg py-16 px-6 flex flex-col items-center text-center" data-testid="workspace-no-sections">
            <ShieldAlert size={28} className="text-slate-400 mb-3" />
            <div className="text-sm font-bold text-slate-700">No project operation sections are available for your role</div>
            <div className="text-xs text-slate-500 mt-1 max-w-md">
              Ask an administrator to grant project operations permissions to access this workspace.
            </div>
          </div>
        )}
        {readOnly && (
          <div className="mb-3 text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded px-3 py-2" data-testid="workspace-readonly-banner">
            Project Operations is switched OFF for this project — history remains visible in <b>read-only</b> mode; new transactions are blocked until an administrator re-enables it.
          </div>
        )}
        {tabs.some((t) => t.key === "boq") && visited.has("boq") && (
          <div style={{ display: active === "boq" ? "block" : "none" }} data-testid="workspace-panel-boq">
            {showTabs ? <ProjectBoqTab project={project} /> : <OpsDisabledNotice testid="workspace-boq-ops-disabled" />}
          </div>
        )}
        {tabs.some((t) => t.key === "vendor") && visited.has("vendor") && (
          <div style={{ display: active === "vendor" ? "block" : "none" }} data-testid="workspace-panel-vendor">
            {showTabs ? <ProjectPRTab project={project} /> : <OpsDisabledNotice testid="workspace-vendor-ops-disabled" />}
          </div>
        )}
        {tabs.some((t) => t.key === "grn") && visited.has("grn") && (
          <div style={{ display: active === "grn" ? "block" : "none" }} data-testid="workspace-panel-grn">
            {showTabs ? <ProjectGRNTab project={project} /> : <OpsDisabledNotice testid="workspace-grn-ops-disabled" />}
          </div>
        )}
        {tabs.some((t) => t.key === "stock") && visited.has("stock") && (
          <div style={{ display: active === "stock" ? "block" : "none" }} data-testid="workspace-panel-stock">
            {showTabs ? <ProjectStockTab project={project} /> : <OpsDisabledNotice testid="workspace-stock-ops-disabled" />}
          </div>
        )}
        {tabs.some((t) => t.key === "progress") && visited.has("progress") && (
          <div style={{ display: active === "progress" ? "block" : "none" }} data-testid="workspace-panel-progress">
            {showTabs ? <ProjectWorkProgressTab project={project} /> : <OpsDisabledNotice testid="workspace-progress-ops-disabled" />}
          </div>
        )}
      </div>
    </div>
  );
}
